import { Link } from '@tanstack/react-router'
import {
  AlarmClockIcon,
  CheckCheckIcon,
  ClockIcon,
  InboxIcon,
  ListTodoIcon,
  type LucideIcon,
  UserRoundCheckIcon,
} from 'lucide-react'
import { ItemsView } from '@/components/items-view'
import { TagFilter } from '@/components/tag-filter'
import { WeekStrip } from '@/components/week-strip'
import { useItems, useSettings } from '@/lib/items'
import { FILTER_LABELS, filterCounts, type ListFilter, listFilters } from '@/lib/lists'
import { hasTag, tagLabel, tagsOf } from '@/lib/tags'
import { cn } from '@/lib/utils'

export function InboxPage() {
  return (
    <ItemsView
      view="inbox"
      title="Inbox"
      description="New signals from your connected apps, waiting to be sorted."
      header={<WeekStrip />}
      empty={{
        title: "You're all caught up",
        text: 'Requests, mentions and promises from Gmail, Chat, GitHub, Jira and more will show up here for a quick sort.',
        icon: InboxIcon,
      }}
    />
  )
}

type ListPage = {
  /** The page's name for `data-view`: each filter is a view of the one list. */
  name: string
  description: string
  empty: { title: string; text: string; icon: LucideIcon }
}

const LIST_PAGES: Record<ListFilter, ListPage> = {
  all: {
    name: 'tasks',
    description: 'Everything you are responsible for, and who waits on it.',
    empty: {
      title: 'No tasks yet',
      text: 'Sort a signal from the inbox, or capture one with C or ⌘K.',
      icon: ListTodoIcon,
    },
  },
  owe: {
    name: 'owe',
    description: 'People waiting on you, with the date you promised.',
    empty: {
      title: 'Nobody is waiting on you',
      text: 'When you promise someone something, set Waiting to "On me". hugo warns you before a promise slips and drafts a heads-up.',
      icon: UserRoundCheckIcon,
    },
  },
  waiting: {
    name: 'waiting',
    description: 'People you need something from.',
    empty: {
      title: "You're not waiting on anyone",
      text: 'When you need something from someone, set Waiting to "On someone". hugo drafts reminders in the original thread and stops once they reply.',
      icon: ClockIcon,
    },
  },
}

/** All, I owe, Waiting on: links, so the filter is in the address and Back works. */
function FilterTabs({
  filter,
  tag,
  counts,
}: {
  filter: ListFilter
  tag: string | undefined
  counts: Record<ListFilter, number> | null
}) {
  return (
    <nav aria-label="Filter" className="flex flex-wrap gap-1" data-testid="list-filter">
      {listFilters.map((option) => {
        const active = option === filter
        return (
          <Link
            key={option}
            to="/tasks"
            search={{ ...(option === 'all' ? {} : { filter: option }), ...(tag ? { tag } : {}) }}
            aria-current={active ? 'page' : undefined}
            data-filter={option}
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-sm font-medium text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50',
              active && 'border-foreground/20 bg-muted text-foreground',
            )}
          >
            {FILTER_LABELS[option]}
            {counts ? (
              <span
                className="text-xs tabular-nums text-muted-foreground"
                data-testid="filter-count"
              >
                {counts[option]}
              </span>
            ) : null}
          </Link>
        )
      })}
    </nav>
  )
}

/**
 * My tasks: one list of everything the user is responsible for (decision 96), by due date. Who
 * waits on an item is shown on its row ("I owe Nina", "Waiting on Marco") and filters the list.
 */
export function TasksPage({ filter, tag }: { filter: ListFilter; tag?: string }) {
  const query = useItems('open')
  const personalTags = useSettings().data?.personalTags
  const page = LIST_PAGES[filter]
  // Counts and tags of what the tag filter shows (the waiting filter counts its own).
  const tagged = (query.data ?? []).filter((item) => hasTag(item, tag))
  const tags = tagsOf(query.data ?? [])
  return (
    <ItemsView
      view="open"
      filter={filter}
      tag={tag}
      name={page.name}
      title="My tasks"
      description={page.description}
      empty={
        tag
          ? {
              ...page.empty,
              title: `Nothing tagged ${tagLabel(tag)} here`,
              text: 'Choose All tags to see the rest.',
            }
          : page.empty
      }
      header={
        <div className="flex flex-col gap-2">
          <FilterTabs filter={filter} tag={tag} counts={query.data ? filterCounts(tagged) : null} />
          {tags.length > 0 || tag ? (
            <TagFilter
              tags={tags}
              active={tag}
              personalTags={personalTags}
              link={(option, props) => (
                <Link
                  to="/tasks"
                  search={{
                    ...(filter === 'all' ? {} : { filter }),
                    ...(option ? { tag: option } : {}),
                  }}
                  {...props}
                />
              )}
            />
          ) : null}
        </div>
      }
    />
  )
}

export function SnoozedPage() {
  return (
    <ItemsView
      view="snoozed"
      title="Snoozed"
      description="Hidden until their time comes; then they return to where they were."
      empty={{
        title: 'Nothing snoozed',
        text: 'Press S on any item to snooze it.',
        icon: AlarmClockIcon,
      }}
    />
  )
}

export function DonePage() {
  return (
    <ItemsView
      view="done"
      title="Done"
      description="Done, dropped and acknowledged items, newest first. Press R to reopen."
      empty={{
        title: 'Nothing done yet',
        text: 'Items you finish or drop show up here.',
        icon: CheckCheckIcon,
      }}
    />
  )
}
